import {createApolloClient} from "../../../providers/ApolloClientFactory";
import {organizationStore} from "../../../store/organization";
import {usdFormat} from "../../../utils";
import {GET_ITEM_CATEGORIES, GET_TICKETING_ITEMS, GET_TICKETING_TABS} from "./queries";
import {Badge} from "../../../components/Badge";
import {
    Link,
    LoaderFunctionArgs,
    useLoaderData,
    useNavigate,
    useRevalidator,
} from "react-router-dom";
import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {eventStore} from "../../../store/event";
import moment from "moment";
import {usePermissions} from "../../auth/api";
import {
    ColDef,
    GridApi,
    SizeColumnsToContentStrategy
} from "ag-grid-community";
import {Input} from "../../../components/inputs/Input";
import {PrimaryButton, SecondaryButton} from "../../../components/Button";
import {AgGridReact} from "ag-grid-react";
import {userStore} from "../../../store/user";
import Switch from "react-switch";
import {TicketingItem, TicketingTab} from "../../../types/tickets";
import {useGridEdit, whereByIdNum} from "../../helpers/GridEditHelper";
import {UPDATE_TICKETING_ITEM} from "./mutations";
import TextSuggestEditor from "../../helpers/TextSuggestEditorHelper";
import {GET_LIST_EVENTS_BY_ORGANIZATION} from "../../../queries";
import GridEditActions from "../../helpers/GridEditActions";
import {useGridSave} from "../../helpers/UseGridSave";

const client = createApolloClient();
const {getState} = userStore;
const {getState: getOrganizationState} = organizationStore;
const {getState: getEventStore} = eventStore;

const buildSearch = (search: string) => [
    {name: {_ilike: `%${search}%`}},
    {category: {_ilike: `%${search}%`}},
];

type CellRenderer = (params: {
    value:
        | string
        | number
        | boolean
        | React.ReactElement
        | React.ReactFragment
        | React.ReactPortal
        | null
        | undefined;
    data: TicketingItem;
}) => JSX.Element | null;

const createCellRenderer = (
    permission: boolean,
    linkFunc: (params: { data: TicketingItem }) => string
): CellRenderer => {
    return (params) => {
        if (params.value === undefined) return null;
        return permission ? (
            <Link to={linkFunc(params)}>
                <Badge>{params.value}</Badge>
            </Link>
        ) : (
            <Badge>{params.value}</Badge>
        );
    };
};

export const ticketingItemsListLoader = async ({ request }: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const search = url.searchParams.get("search");
    const rawOrganizationId = getOrganizationState().organizationId;
    const organizationId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : "", 10);
    const eventId = getEventStore().eventId;
    const user = getState().user;

    let where: any = { _or: search ? buildSearch(search) : undefined };
    if (organizationId && organizationId !== 0) where = { ...where, event: { organization_id: { _eq: organizationId } } };
    if (eventId) where = { ...where, event_id: { _eq: eventId } };
    else if (user?.events) where = { ...where, event_id: { _in: user?.events } };

    let eventsWhere: any = {};
    if (organizationId && organizationId !== 0) {
        eventsWhere.organization_id = { _eq: organizationId };
        eventsWhere.is_active = { _eq: true };
    }
    if (user?.events?.length) eventsWhere.id = { _in: user.events };

    let tabsWhere: any = {};
    if (eventId) tabsWhere = { event_id: { _eq: eventId } };
    else if (user?.events?.length) tabsWhere = { event_id: { _in: user.events } };
    else if (organizationId && organizationId !== 0) tabsWhere = { event: { organization_id: { _eq: organizationId } } };

    const [{ data: itemsRes }, { data: eventsRes }, { data: catsRes }, { data: tabsRes }] = await Promise.all([
        client.query({ query: GET_TICKETING_ITEMS, variables: { where } }),
        client.query({ query: GET_LIST_EVENTS_BY_ORGANIZATION, variables: { where: eventsWhere } }),
        client.query({
            query: GET_ITEM_CATEGORIES,
            variables: {
                where: organizationId
                    ? {
                        organization_id: { _in: [0, organizationId] },
                        is_active: { _eq: true },
                        reference_type: { _in: ["item_ticketing_category", "item_category"] },
                    }
                    : {},
            },
        }),
        client.query({ query: GET_TICKETING_TABS, variables: { where: tabsWhere } }),
    ]);

    return {
        items: itemsRes.items_ticketing as TicketingItem[],
        events: eventsRes.events as { id: number; name: string }[],
        categories: catsRes.dashboard_reference as { reference_label: string; reference_value: string }[],
        tabs: (tabsRes?.items_ticketing_tabs ?? []) as TicketingTab[],
    };
};

export const TicketingItemList = () => {
    const {ItemsPermission} = usePermissions("ticketingReporting");
    const {editCreatePermission: eventsEditPermission} = usePermissions("events");
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const gridRef = useRef<GridApi>();
    const shellRef = useRef<HTMLDivElement>(null);

    // Match the Items page: measure the real top of the shell rather than
    // assuming a fixed header height. The old `calc(100vh - 128px)` guessed an
    // offset that is wrong whenever the app header wraps to two or three rows,
    // which cost the grid vertical space and pushed the pagination bar down.
    const [shellHeight, setShellHeight] = useState("calc(100vh - 148px)");
    useEffect(() => {
        const measure = () => {
            const el = shellRef.current;
            if (!el) return;
            const docTop = el.getBoundingClientRect().top + window.scrollY;
            setShellHeight(`${Math.max(360, window.innerHeight - docTop - 16)}px`);
        };
        measure();
        window.addEventListener("resize", measure);
        window.addEventListener("orientationchange", measure);
        return () => {
            window.removeEventListener("resize", measure);
            window.removeEventListener("orientationchange", measure);
        };
    }, []);
    const originalActiveRef = useRef<Map<string, boolean>>(new Map());

    const { items, events, categories, tabs } = useLoaderData() as {
        items: TicketingItem[];
        events: { id: number; name: string }[];
        categories: { reference_label: string; reference_value: string }[];
        tabs: TicketingTab[];
    };

    const [rowData, setRowData] = useState<TicketingItem[]>([]);
    const [isActiveToggle, setIsActiveToggle] = useState(true);

    const {pending, pendingCount, resetBaseline, makeEditable, setPending, getBaselineRows} =
        useGridEdit<TicketingItem>();

    const getRowId = useCallback((p: { data: TicketingItem }) => String(p.data.id), []);

    const mutateRow = async (id: number, patch: Partial<TicketingItem>) => {
        const where = whereByIdNum(id);
        const user = getState().user;
        const input = {
            ...patch,
            last_updated_by: user?.id,
            updated_at: new Date().toISOString()
        };
        const res = await client.mutate({
            mutation: UPDATE_TICKETING_ITEM,
            variables: { input, where },
            errorPolicy: "all",
        });
        if (res.errors?.length) throw new Error(res.errors.map(e => e.message).join("; "));
    };

    const { handleSave } = useGridSave<TicketingItem>({
        rowData,
        setRowData,
        pending,
        setPending,
        resetBaseline,
        gridApiRef: gridRef as any,
        revalidate: () => revalidator.revalidate(),
        mutateRow,
        optimistic: true,
        baselines: [
            { ref: originalActiveRef, value: (r) => !!r.is_active },
        ],
    });

    const getPatchedValue = (p: any, field?: string) => {
        if (!field) return p.value;
        const id = String(p.data?.id ?? "");
        const patch = p.context?.pending?.[id];
        if (patch && Object.prototype.hasOwnProperty.call(patch, field)) {
            return (patch as any)[field];
        }
        return p.data?.[field];
    };

    useEffect(() => {
        resetBaseline(items);
        originalActiveRef.current = new Map(
            (items ?? []).map(x => [String(x.id), !!x.is_active])
        );
        setRowData(items);
    }, [items, resetBaseline]);

    useEffect(() => {
        const api = gridRef.current;
        if (!api) return;
        const model = isActiveToggle
            ? { is_active: { filterType: 'text', type: 'equals', filter: 'Yes' } }
            : null;
        api.setFilterModel(model);
        api.onFilterChanged();
    }, [isActiveToggle, pendingCount]);

    useEffect(() => {
        gridRef.current?.refreshCells({force: true});
        gridRef.current?.refreshClientSideRowModel("filter");
    }, [pending]);

    const idRenderer = useMemo(
        () =>
            createCellRenderer(ItemsPermission, (p) => `/ticketing-items/${p.data.id}/edit`),
        [ItemsPermission]
    );

    const nameRenderer = useMemo(
        () =>
            createCellRenderer(ItemsPermission, (p) => `/ticketing-items/${p.data.id}/edit`),
        [ItemsPermission]
    );

    const eventIdToName = useMemo(
        () => new Map<number, string>((events ?? []).map(e => [Number(e.id), String(e.name ?? "").trim()])),
        [events]
    );
    const eventNameToId = useMemo(
        () => new Map<string, number>((events ?? []).map(e => [String(e.name ?? "").trim(), Number(e.id)])),
        [events]
    );
    const eventNameOptions = useMemo(
        () => Array.from(eventNameToId.keys()).sort((a, b) => a.localeCompare(b)),
        [eventNameToId]
    );

    const catValueToLabel = useMemo(
        () =>
            new Map<string, string>(
                (categories ?? [])
                    .map(c => [
                        String(c.reference_value ?? "").trim(),
                        String(c.reference_label ?? "").trim(),
                    ] as const)
                    .filter(([val, lab]) => !!val && !!lab)
            ),
        [categories]
    );

    const catLabelToValue = useMemo(
        () =>
            new Map<string, string>(
                (categories ?? [])
                    .map(c => [
                        String(c.reference_label ?? "").trim(),
                        String(c.reference_value ?? "").trim(),
                    ] as const)
                    .filter(([lab, val]) => !!lab && !!val)
            ),
        [categories]
    );

    const categoryOptions = useMemo(() => {
        const fromRef = (categories ?? [])
            .map(c => String(c.reference_label ?? "").trim())
            .filter(Boolean);

        const fromData = Array.from(
            new Set(
                (items ?? [])
                    .map(i => String(i.category ?? "").trim())
                    .filter(Boolean)
            )
        );

        return Array.from(new Set([...fromRef, ...fromData])).sort((a, b) =>
            a.localeCompare(b)
        );
    }, [categories, items]);

    // Tabs are event-scoped: the Tab editor only offers tabs from the row's event.
    const tabIdToName = useMemo(
        () => new Map<number, string>((tabs ?? []).map(t => [Number(t.id), String(t.name ?? "").trim()])),
        [tabs]
    );
    const tabsByEvent = useMemo(() => {
        const m = new Map<number, TicketingTab[]>();
        (tabs ?? []).forEach(t => {
            const list = m.get(Number(t.event_id)) ?? [];
            list.push(t);
            m.set(Number(t.event_id), list);
        });
        return m;
    }, [tabs]);
    const tabNameOptionsFor = (eventId: number) =>
        (tabsByEvent.get(eventId) ?? []).map(t => t.name);
    const tabIdFor = (eventId: number, name: string) =>
        (tabsByEvent.get(eventId) ?? []).find(
            t => String(t.name ?? "").trim().toLowerCase() === name.trim().toLowerCase()
        )?.id ?? null;

    const canEditTicketingItem = !!ItemsPermission;

    const colDefs: ColDef<TicketingItem>[] = useMemo(
        () => [
            {
                headerName: "Id",
                field: "id",
                cellRenderer: idRenderer,
                filter: "agTextColumnFilter",
                pinned: "left",
                editable: false,
            },
            {
                headerName: "Item Name",
                field: "name",
                cellRenderer: nameRenderer,
                filter: "agTextColumnFilter",
                pinned: "left",
                ...makeEditable("name",
                    {
                        type: "text",
                        canEdit: canEditTicketingItem,
                    }, setRowData),
            },
            {
                headerName: "Is Active?",
                field: "is_active",
                ...makeEditable(
                    "is_active",
                    {
                        type: "boolean",
                        labels: {on: "Yes", off: "No"},
                        canEdit: canEditTicketingItem,
                        toggleOnClick: true
                    }, setRowData),
                valueFormatter: (p) => (getPatchedValue(p, "is_active") ? "Yes" : "No"),
                filter: "agTextColumnFilter",
                filterValueGetter: (p: any) => {
                    const id = String(p.data?.id ?? "");
                    const patch = p.context?.pending?.[id] as { is_active?: boolean } | undefined;
                    if (patch && Object.prototype.hasOwnProperty.call(patch, "is_active")) {
                        return patch.is_active ? "Yes" : "No";
                    }
                    const baseline = originalActiveRef.current.get(id);
                    return baseline ? "Yes" : "No";
                },
            },
            {
                headerName: "Available for Public Sale?",
                field: "available_public_sale",
                ...makeEditable(
                    "available_public_sale",
                    {
                        type: "boolean",
                        labels: {on: "Yes", off: "No"},
                        canEdit: canEditTicketingItem,
                        toggleOnClick: true
                    }, setRowData),
                valueFormatter: (p) => (p.data?.available_public_sale ? "Yes" : "No"),
                filter: "agTextColumnFilter",
                filterValueGetter: (p) => (getPatchedValue(p, "available_public_sale") ? "Yes" : "No"),
            },
            {
                headerName: "Cashless Top Up Item?",
                field: "cashless_top_up_item",
                ...makeEditable(
                    "cashless_top_up_item",
                    {
                        type: "boolean",
                        labels: {on: "Yes", off: "No"},
                        canEdit: canEditTicketingItem,
                        toggleOnClick: true
                    }, setRowData),
                valueFormatter: (p) => (p.data?.cashless_top_up_item ? "Yes" : "No"),
                filter: "agTextColumnFilter",
                filterValueGetter: (p) => (getPatchedValue(p, "cashless_top_up_item") ? "Yes" : "No"),
            },
            {
                headerName: "Price",
                field: "price",
                ...makeEditable(
                    "price",
                    {
                        type: "number",
                        canEdit: canEditTicketingItem,
                        precision: 2,
                        displayFactor: 100,
                        format: (displayValue) =>
                            Number.isFinite(displayValue) ? displayValue.toFixed(2) : "0.00",
                    }, setRowData),
                filter: "agTextColumnFilter",
                valueFormatter: (p) => usdFormat.format((p.value ?? 0) / 100),
                filterValueGetter: (p) =>
                    usdFormat.format((Number(getPatchedValue(p, "price")) ?? 0) / 100),
            },
            {
                headerName: "Tax Percentage",
                field: "tax_percentage",
                ...makeEditable(
                    "tax_percentage",
                    {
                        type: "number",
                        canEdit: canEditTicketingItem,
                        precision: 6
                    }, setRowData),
                filter: "agTextColumnFilter",
                valueFormatter: (p) => {
                    const val = Number(p.value);
                    return Number.isFinite(val) ? val.toFixed(6) : "0.000000";
                },
                filterValueGetter: (p) => {
                    const val = Number(getPatchedValue(p, "tax_percentage"));
                    return Number.isFinite(val) ? val.toFixed(6) : "0.000000";
                },
            },
            {
                headerName: "Category",
                field: "category",
                cellEditor: TextSuggestEditor,
                cellEditorParams: (p: { value: any; data: TicketingItem }) => ({
                    values: categoryOptions,
                    showAllOnEdit: true,
                    initialValue:
                        catValueToLabel.get(String(p.value ?? p.data?.category ?? "").trim()) ??
                        String(p.value ?? p.data?.category ?? ""),
                }),
                ...makeEditable(
                    "category",
                    {
                        type: "text",
                        canEdit: canEditTicketingItem,
                        parser: (nv: any, ov: any) => {
                            const s = String(nv ?? "").trim();
                            if (!s) return ov;
                            const byLabel = catLabelToValue.get(s);
                            return byLabel ?? s;
                        },
                        formatter: (v: any) =>
                            catValueToLabel.get(String(v ?? "").trim()) ??
                            String(v ?? "").trim(),
                        equals: (a: any, b: any) =>
                            String(a ?? "").trim() === String(b ?? "").trim(),
                    },
                    setRowData
                ),
                filter: "agTextColumnFilter",
                filterValueGetter: (p) =>
                    catValueToLabel.get(String((p.data as TicketingItem).category ?? "").trim()) ??
                    String((p.data as TicketingItem).category ?? "").trim(),
            },
            {
                headerName: "Tab",
                field: "tab_id",
                editable: canEditTicketingItem,
                cellEditor: TextSuggestEditor,
                cellEditorParams: (p: { value: any; data: TicketingItem }) => ({
                    values: tabNameOptionsFor(Number(p.data?.event_id)),
                    showAllOnEdit: true,
                    initialValue: tabIdToName.get(Number(p.value ?? p.data?.tab_id)) ?? "",
                }),
                // Tabs are event-scoped, so the typed name resolves against the
                // row's own event. Blank clears the tab; an unknown name is ignored.
                valueParser: (p: any) => p.newValue,
                valueSetter: (p: any) => {
                    const row = p.data as TicketingItem;
                    const typed = String(p.newValue ?? "").trim();
                    const next: number | null = typed ? tabIdFor(Number(row.event_id), typed) : null;
                    if (typed && next === null) return false;
                    const current = row.tab_id ?? null;
                    if (next === current) return false;
                    const baseline = (items ?? []).find(i => i.id === row.id)?.tab_id ?? null;
                    setRowData(prev => prev.map(r => (r.id === row.id ? {...r, tab_id: next} : r)));
                    setPending(prev => {
                        const copy: any = {...prev};
                        if (next === baseline) {
                            if (copy[row.id]) {
                                delete copy[row.id].tab_id;
                                if (Object.keys(copy[row.id]).length === 0) delete copy[row.id];
                            }
                        } else {
                            copy[row.id] = {...(copy[row.id] || {}), tab_id: next};
                        }
                        return copy;
                    });
                    return true;
                },
                filter: "agTextColumnFilter",
                filterValueGetter: (p) => tabIdToName.get(Number(getPatchedValue(p, "tab_id"))) ?? "",
                valueFormatter: (p) => tabIdToName.get(Number(p.value)) ?? "",
            },
            {
                headerName: "Sort Order",
                field: "sort_order",
                ...makeEditable("sort_order",
                    {
                        type: "number",
                        canEdit: canEditTicketingItem,
                        precision: 0
                    }, setRowData),
                filter: "agTextColumnFilter",
            },
            {
                headerName: "Event",
                field: "event_id",
                cellEditor: TextSuggestEditor,
                cellEditorParams: (p: { value: any; data: TicketingItem }) => ({
                    values: eventNameOptions,
                    showAllOnEdit: true,
                    initialValue:
                        eventIdToName.get(
                            Number(p.value ?? p.data?.event_id ?? (p as any)?.data?.event?.id)
                        ) ?? (p as any)?.data?.event?.name ?? "",
                }),
                ...makeEditable(
                    "event_id",
                    {
                        type: "text",
                        canEdit: canEditTicketingItem,
                        parser: (nv: any, ov: any) => {
                            const s = String(nv ?? "").trim();
                            if (!s) return ov;
                            const byName = eventNameToId.get(s);
                            if (byName != null) return byName;
                            const n = Number(s);
                            return (Number.isFinite(n) && eventIdToName.has(n)) ? n : ov;
                        },
                        equals: (a: any, b: any) => Number(a ?? 0) === Number(b ?? 0),
                    },
                    setRowData
                ),
                cellRenderer: (p: { value: any; data: TicketingItem }) => {
                    const id = Number(p.value ?? p.data?.event_id ?? (p as any)?.data?.event?.id);
                    const name = eventIdToName.get(id) ?? (p as any)?.data?.event?.name ?? "";
                    if (!name) return null;
                    return eventsEditPermission ? (
                        <Link to={`/events/${id}/edit`} onMouseDown={(e) => e.stopPropagation()}>
                            <Badge>{name}</Badge>
                        </Link>
                    ) : (
                        <Badge>{name}</Badge>
                    );
                },
                filter: "agTextColumnFilter",
                valueGetter: (p) => {
                    const id = Number(p.data?.event_id ?? (p.data as any)?.event?.id);
                    return eventIdToName.get(id) ?? (p.data as any)?.event?.name ?? "";
                },
            },
            {
                headerName: "Sales Limit",
                field: "sales_limit",
                ...makeEditable("sales_limit",
                    {
                        type: "number",
                        canEdit: canEditTicketingItem,
                        precision: 0
                    }, setRowData),
                filter: "agTextColumnFilter",
            },
            {
                headerName: "Tickets Remaining",
                field: "sales_limit_balance",
                ...makeEditable("sales_limit_balance",
                    {
                        type: "number",
                        canEdit: canEditTicketingItem,
                        precision: 0
                    }, setRowData),
                filter: "agTextColumnFilter",
            },
            {
                headerName: "Order Quantity Limit",
                field: "order_quantity_limit",
                ...makeEditable("order_quantity_limit",
                    {
                        type: "number",
                        canEdit: canEditTicketingItem,
                        precision: 0
                    }, setRowData),
                filter: "agTextColumnFilter",
            },
            {
                headerName: "Unlimited Ingress?",
                field: "unlimited_ingress",
                ...makeEditable(
                    "unlimited_ingress",
                    {
                        type: "boolean",
                        labels: {on: "Yes", off: "No"},
                        canEdit: canEditTicketingItem,
                        toggleOnClick: true
                    }, setRowData),
                valueFormatter: (p) => (p.data?.unlimited_ingress ? "Yes" : "No"),
                filter: "agTextColumnFilter",
                filterValueGetter: (p) => (getPatchedValue(p, "unlimited_ingress") ? "Yes" : "No"),
            },
            {
                headerName: "Include Egress?",
                field: "include_egress",
                ...makeEditable(
                    "include_egress",
                    {
                        type: "boolean",
                        labels: {on: "Yes", off: "No"},
                        canEdit: canEditTicketingItem,
                        toggleOnClick: true
                    }, setRowData),
                valueFormatter: (p) => (p.data?.include_egress ? "Yes" : "No"),
                filter: "agTextColumnFilter",
                filterValueGetter: (p) => (getPatchedValue(p, "include_egress") ? "Yes" : "No"),
            },
            {
                headerName: "Include POS Tokens?",
                field: "include_pos_tokens",
                ...makeEditable(
                    "include_pos_tokens",
                    {
                        type: "boolean",
                        labels: {on: "Yes", off: "No"},
                        canEdit: canEditTicketingItem,
                        toggleOnClick: true
                    }, setRowData),
                valueFormatter: (p) => (p.data?.include_pos_tokens ? "Yes" : "No"),
                filter: "agTextColumnFilter",
                filterValueGetter: (p) => (getPatchedValue(p, "include_pos_tokens") ? "Yes" : "No"),
            },
            {
                headerName: "Updated By",
                field: "user.username",
                valueGetter: (p) => p.data?.user?.username,
                filter: "agTextColumnFilter",
                editable: false,
            },
            {
                headerName: "Created Date",
                field: "created_at",
                valueFormatter: (p) => {
                    const d = p.value ? moment(p.value) : null;
                    return d ? d.format("MM/DD/YYYY h:mm:ss a") : "";
                },
                editable: false,
                filter: "agTextColumnFilter",
                filterValueGetter: (p) =>
                    p.data?.created_at ? moment(p.data.created_at).format("MM/DD/YYYY h:mm:ss a") : "",
            },
            {
                headerName: "Updated Date",
                field: "updated_at",
                valueFormatter: (p) => {
                    const d = p.value ? moment(p.value) : null;
                    return d ? d.format("MM/DD/YYYY h:mm:ss a") : "";
                },
                editable: false,
                filter: "agTextColumnFilter",
                filterValueGetter: (p) =>
                    p.data?.updated_at ? moment(p.data.updated_at).format("MM/DD/YYYY h:mm:ss a") : "",
            },
        ],
        [
            idRenderer, nameRenderer, makeEditable, setRowData,
            ItemsPermission, eventsEditPermission,
            categoryOptions, eventIdToName, eventNameToId, eventNameOptions,
            tabIdToName, tabsByEvent, items, setPending
        ]
    );

    const defaultColDef = useMemo(() => ({
        valueGetter: (p: any) => p.colDef.field ? getPatchedValue(p, p.colDef.field) : p.value,
    }), []);

    const rowHeight = 50;
    const autoSizeStrategy: SizeColumnsToContentStrategy = {type: "fitCellContents"};
    const gridOptions = useMemo(() => ({enableCellTextSelection: true, context: {pending}}), [pending]);

    const handleExportCSV = () => {
        if (gridRef.current) {
            const timestamp = new Date().toISOString().replace(/[-:]/g, "");
            gridRef.current.exportDataAsCsv({
                fileName: `Items_${timestamp}.csv`,
                columnSeparator: ",",
            });
        }
    };

    return (
        <div
            ref={shellRef}
            className="w-full flex flex-col overflow-hidden"
            style={{ height: shellHeight }}
            aria-busy={revalidator.state === "loading"}
        >
            <div className="relative bg-white rounded-md shadow-sm px-3 md:px-4 pt-1 pb-3 mb-2 mt-3">
            {revalidator.state === "loading" && (
                <div
                    className="absolute inset-x-0 top-0 h-[3px] overflow-hidden rounded-t-md bg-primary-100"
                    aria-hidden="true"
                >
                    <div className="ronin-loading-bar h-full w-1/5 bg-primary-600"/>
                </div>
            )}
            <div className="md:hidden grid grid-cols-6 min-w-0 px-0 py-0">
                <div className="col-span-6 flex items-center justify-between min-h-[48px] mb-1">
                    <div className="flex items-center gap-2 pl-2 pt-2">
                        <GridEditActions
                            pendingCount={pendingCount}
                            pending={pending}
                            gridRef={gridRef}
                            resetBaseline={resetBaseline}
                            baselineData={items}
                            getBaselineRows={getBaselineRows}
                            isActiveToggle={isActiveToggle}
                            setRowData={setRowData}
                            setPending={setPending}
                            onSave={handleSave}
                        />
                    </div>

                    <div className="ml-2 flex flex-col items-center">
                        <span className="block text-xs font-medium text-gray-500 whitespace-nowrap">Active Only</span>
                        <Switch
                            checked={isActiveToggle}
                            onChange={() => setIsActiveToggle((prev) => !prev)}
                            onColor="#EE2E30"
                            onHandleColor="#FFFFFF"
                            handleDiameter={18}
                            uncheckedIcon={false}
                            checkedIcon={false}
                            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                            activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                            height={15}
                            width={48}
                        />
                    </div>
                </div>

                <div className="col-span-6 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                        <div className="w-full px-3 py-2">
                            <Input
                                name="search"
                                placeholder="Search Ticketing Items"
                                onChange={(s) => {
                                    navigate(s && typeof s === "string" && s.length > 0 ? `/ticketing-items/list?search=${s}` : `/ticketing-items/list`);
                                    revalidator.revalidate();
                                }}
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                        <SecondaryButton onClick={handleExportCSV}>Export to CSV</SecondaryButton>
                        {ItemsPermission && (
                            <SecondaryButton onClick={() => navigate(`/ticketing-setup`)}>Setup</SecondaryButton>
                        )}
                        {ItemsPermission && (
                            <PrimaryButton onClick={() => navigate(`/ticketing-items/create`)}>Create</PrimaryButton>
                        )}
                    </div>
                </div>
            </div>

            <div className="hidden md:grid grid-cols-12 items-center gap-3 min-w-0 py-2">
                <div className="col-span-5 min-w-0">
                    <div className="w-full">
                        <Input
                            name="search"
                            placeholder="Search Ticketing Items"
                            onChange={(s) => {
                                navigate(s && typeof s === "string" && s.length > 0 ? `/ticketing-items/list?search=${s}` : `/ticketing-items/list`);
                                revalidator.revalidate();
                            }}
                        />
                    </div>
                </div>

                <div className="col-span-3 col-start-6 justify-self-start flex items-center gap-1">
                    <GridEditActions
                        pendingCount={pendingCount}
                        pending={pending}
                        gridRef={gridRef}
                        resetBaseline={resetBaseline}
                        baselineData={items}
                        getBaselineRows={getBaselineRows}
                        isActiveToggle={isActiveToggle}
                        setRowData={setRowData}
                        setPending={setPending}
                        onSave={handleSave}
                    />
                </div>

                <div className="col-span-1 flex flex-col items-center">
                    <span className="block text-sm font-medium text-gray-500 whitespace-nowrap">Active Only</span>
                    <Switch
                        checked={isActiveToggle}
                        onChange={() => setIsActiveToggle((prev) => !prev)}
                        onColor="#EE2E30"
                        onHandleColor="#FFFFFF"
                        handleDiameter={18}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                        activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                        height={15}
                        width={48}
                    />
                </div>

                <div className="col-span-3 flex justify-end items-center gap-1 whitespace-nowrap">
                    <SecondaryButton onClick={handleExportCSV}>
                        <span className="whitespace-nowrap">Export&nbsp;to&nbsp;CSV</span>
                    </SecondaryButton>
                    {ItemsPermission && (
                        <SecondaryButton onClick={() => navigate(`/ticketing-setup`)}>
                            <span className="whitespace-nowrap">Ticketing&nbsp;Setup</span>
                        </SecondaryButton>
                    )}
                    {ItemsPermission && (
                        <PrimaryButton onClick={() => navigate(`/ticketing-items/create`)}>Create</PrimaryButton>
                    )}
                </div>
            </div>

            </div>

            <div className="flex-1 min-h-0">
                <div className="ag-theme-quartz w-full h-full min-w-0">
                    <AgGridReact
                        rowData={rowData}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        rowHeight={rowHeight}
                        autoSizeStrategy={autoSizeStrategy}
                        pagination
                        gridOptions={gridOptions}
                        paginationPageSize={100}
                        getRowId={getRowId}
                        onGridReady={(params) => {
                            gridRef.current = params.api;
                            if (isActiveToggle) {
                                const model = { is_active: { filterType: 'text', type: 'equals', filter: 'Yes' } };
                                params.api.setFilterModel(model);
                                params.api.onFilterChanged();
                            }
                        }}
                        singleClickEdit
                        stopEditingWhenCellsLoseFocus
                    />
                </div>
            </div>
        </div>
    );
};
